import {
  COMPANY_LABELS,
  SOCIAL_ACCOUNT_KIND_LABELS,
  SOCIAL_PLATFORM_LABELS,
  formatNumber,
  type SocialAccountDto,
} from '@nova/shared';
import { ArrowDownRight, ArrowRight, ArrowUpRight, Facebook, Linkedin } from 'lucide-react';
import { Area, AreaChart, ResponsiveContainer, YAxis } from 'recharts';
import { useChartTheme } from '../../app/ThemeProvider.js';
import { areaColorFor } from '../../lib/area-color.js';
import { cn } from '../../lib/cn.js';
import { EmptyState } from '../../components/EmptyState.js';
import { GlassPanel } from '../../components/GlassPanel.js';
import { SkeletonPanel } from '../../components/Skeleton.js';
import { SocialHealthBadge } from './SocialHealthBadge.js';

export function SocialOverview({
  accounts,
  isLoading,
}: {
  accounts: SocialAccountDto[];
  isLoading: boolean;
}) {
  if (isLoading) {
    return (
      <div className="grid gap-3 sm:grid-cols-2 xl:grid-cols-3">
        {Array.from({ length: 6 }).map((_, i) => (
          <SkeletonPanel key={i} lines={3} />
        ))}
      </div>
    );
  }

  if (accounts.length === 0) {
    return (
      <GlassPanel>
        <EmptyState
          title="Ingen kontoer å overvåke ennå."
          description="Legg til kontoene under «Register» — så dukker tallene opp her."
        />
      </GlassPanel>
    );
  }

  return (
    <div className="grid gap-3 sm:grid-cols-2 xl:grid-cols-3">
      {accounts.map((account) => (
        <AccountCard key={account.id} account={account} />
      ))}
    </div>
  );
}

function AccountCard({ account }: { account: SocialAccountDto }) {
  const chart = useChartTheme();
  const stroke = account.area ? areaColorFor(account.area.color, chart.theme) : chart.accent;
  const Platform = account.platform === 'FACEBOOK' ? Facebook : Linkedin;

  const followers = account.latest?.followers ?? null;
  const growth = account.followerGrowth;
  const series = account.trend
    .filter((t) => t.followers !== null)
    .map((t) => ({ value: t.followers as number }));

  const Arrow =
    growth === null || growth === 0 ? ArrowRight : growth > 0 ? ArrowUpRight : ArrowDownRight;

  return (
    <GlassPanel accentColor={account.area?.color} className="flex flex-col gap-3">
      <div className="flex items-start justify-between gap-2">
        <div className="min-w-0">
          <div className="text-ink-soft flex items-center gap-1.5 text-[11px]">
            <Platform size={13} aria-hidden />
            <span>{SOCIAL_PLATFORM_LABELS[account.platform]}</span>
            <span aria-hidden>·</span>
            <span>{SOCIAL_ACCOUNT_KIND_LABELS[account.kind]}</span>
          </div>
          <h3 className="font-display text-ink mt-1 truncate text-sm" title={account.name}>
            {account.name}
          </h3>
          <p className="text-ink-soft text-[11px]">{COMPANY_LABELS[account.company]}</p>
        </div>
        <SocialHealthBadge flag={account.health} />
      </div>

      <div className="flex items-end justify-between gap-3">
        <div>
          <div className="flex items-end gap-2">
            <span className="num-tabular font-display text-ink text-[26px] font-bold leading-none">
              {followers === null ? '–' : formatNumber(followers)}
            </span>
            <span className="text-ink-soft pb-0.5 text-[11px]">følgere</span>
          </div>
          <div className="mt-1 flex items-center gap-2 text-[11px]">
            {growth !== null && (
              <span
                className={cn(
                  'inline-flex items-center gap-0.5',
                  growth > 0 ? 'text-success' : growth < 0 ? 'text-danger' : 'text-ink-soft',
                )}
              >
                <Arrow size={12} aria-hidden />
                {growth > 0 ? '+' : ''}
                {formatNumber(growth)}
              </span>
            )}
            <span className="text-ink-soft">{account.latestPosts ?? 0} innlegg siste måned</span>
          </div>
        </div>

        {series.length > 1 && (
          <div className="h-9 w-24 shrink-0" aria-hidden>
            <ResponsiveContainer width="100%" height="100%">
              <AreaChart data={series} margin={{ top: 2, right: 0, bottom: 0, left: 0 }}>
                <defs>
                  <linearGradient id={`some-spark-${account.id}`} x1="0" y1="0" x2="0" y2="1">
                    <stop offset="0%" stopColor={stroke} stopOpacity={0.35} />
                    <stop offset="100%" stopColor={stroke} stopOpacity={0} />
                  </linearGradient>
                </defs>
                <YAxis hide domain={sparklineDomain(series)} />
                <Area
                  type="monotone"
                  dataKey="value"
                  stroke={stroke}
                  strokeWidth={2}
                  fill={`url(#some-spark-${account.id})`}
                  dot={false}
                  isAnimationActive={false}
                />
              </AreaChart>
            </ResponsiveContainer>
          </div>
        )}
      </div>
    </GlassPanel>
  );
}

/** Data range with 12 % headroom so a steady climb is not flattened to a line. */
function sparklineDomain(series: { value: number }[]): [number, number] {
  const values = series.map((d) => d.value);
  const min = Math.min(...values);
  const max = Math.max(...values);
  const padding = (max - min || Math.abs(max) || 1) * 0.12;
  return [min - padding, max + padding];
}
